import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { GroupMember } from '@/types';
import { cn, getProfileColor, formatRelativeTime } from '@/lib/utils';

interface GroupMemberTileProps {
  member: GroupMember;
  canManage: boolean;
  isOwner: boolean;
  currentUserId?: string;
  onApprove?: (userId: string) => void;
  onReject?: (userId: string) => void;
  onRemove?: (userId: string) => void;
  onBan?: (userId: string) => void;
  onUnban?: (userId: string) => void;
  onMute?: (userId: string) => void;
  onUnmute?: (userId: string) => void;
  onPromote?: (userId: string) => void;
  onDemote?: (userId: string) => void;
  onTransfer?: (userId: string) => void;
}

export function GroupMemberTile({
  member,
  canManage,
  isOwner,
  currentUserId,
  onApprove,
  onReject,
  onRemove,
  onBan,
  onUnban,
  onMute,
  onUnmute,
  onPromote,
  onDemote,
  onTransfer,
}: GroupMemberTileProps) {
  const [showMenu, setShowMenu] = useState(false);
  const isMe = member.userId === currentUserId;
  const isPending = member.status === 'pending_invite' || member.status === 'requested';
  const isBanned = member.status === 'banned';
  const isMuted = member.status === 'muted';

  return (
    <div className="flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors rounded-lg">
      {/* Avatar */}
      <Link to={`/profile/${member.userId}`}>
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm"
          style={{ backgroundColor: getProfileColor(member.user?.profileColor) }}
        >
          {(member.user?.displayName || '?').charAt(0).toUpperCase()}
        </div>
      </Link>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Link
            to={`/profile/${member.userId}`}
            className="font-medium text-card-foreground hover:text-primary transition-colors truncate"
          >
            {member.user?.displayName || 'Unknown'}
          </Link>
          {member.role === 'owner' && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-primary/10 text-primary font-medium">
              Owner
            </span>
          )}
          {member.role === 'moderator' && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-blue-500/10 text-blue-600 font-medium">
              Mod
            </span>
          )}
          {isMuted && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-yellow-500/10 text-yellow-600 font-medium">
              Muted
            </span>
          )}
          {isBanned && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500/10 text-red-600 font-medium">
              Banned
            </span>
          )}
          {isMe && (
            <span className="text-xs text-muted-foreground">(you)</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {isPending
            ? member.status === 'pending_invite'
              ? 'Invited'
              : 'Requested to join'
            : member.joinedAt
              ? `Joined ${formatRelativeTime(member.joinedAt)}`
              : ''}
        </p>
      </div>

      {/* Pending actions */}
      {isPending && canManage && (
        <div className="flex gap-2">
          <button
            onClick={() => onApprove?.(member.userId)}
            className="text-xs px-3 py-1.5 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() => onReject?.(member.userId)}
            className="text-xs px-3 py-1.5 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
          >
            Reject
          </button>
        </div>
      )}

      {/* Action menu for active members */}
      {!isPending && canManage && !isMe && member.role !== 'owner' && (
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
          >
            <MoreIcon className="w-4 h-4 text-muted-foreground" />
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 z-20 w-44 bg-card border border-border rounded-lg shadow-lg py-1">
                {isBanned ? (
                  <MenuItem onClick={() => { onUnban?.(member.userId); setShowMenu(false); }}>
                    Unban
                  </MenuItem>
                ) : (
                  <>
                    {isMuted ? (
                      <MenuItem onClick={() => { onUnmute?.(member.userId); setShowMenu(false); }}>
                        Unmute
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={() => { onMute?.(member.userId); setShowMenu(false); }}>
                        Mute
                      </MenuItem>
                    )}
                    {isOwner && member.role === 'member' && (
                      <MenuItem onClick={() => { onPromote?.(member.userId); setShowMenu(false); }}>
                        Promote to Moderator
                      </MenuItem>
                    )}
                    {isOwner && member.role === 'moderator' && (
                      <MenuItem onClick={() => { onDemote?.(member.userId); setShowMenu(false); }}>
                        Demote to Member
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => { onRemove?.(member.userId); setShowMenu(false); }}
                      danger
                    >
                      Remove
                    </MenuItem>
                    <MenuItem
                      onClick={() => { onBan?.(member.userId); setShowMenu(false); }}
                      danger
                    >
                      Ban
                    </MenuItem>
                    {isOwner && (
                      <MenuItem
                        onClick={() => { onTransfer?.(member.userId); setShowMenu(false); }}
                        danger
                      >
                        Transfer Ownership
                      </MenuItem>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-3 py-2 text-sm transition-colors',
        danger
          ? 'text-red-500 hover:bg-red-500/10'
          : 'text-card-foreground hover:bg-muted'
      )}
    >
      {children}
    </button>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
  );
}
